/**
 * Static catalogue mapping for the managing prompt library.
 *
 * Until the backend `ManagingPromptResponse` carries `recordTypes`,
 * `requiresRecord`, and `chatEnabled`, the client enriches the catalogue
 * locally based on the spec table (§10.2).
 */

import type {
  CrmRecordType,
  ManagingPromptCatalogEntry,
  ManagingPromptCategory,
} from "../types/chat.types";

export const CHAT_PROMPT_CUSTOM_TYPE = "custom";

/** Must match typecrm-ai AgentLoopService.EMPTY_ASSISTANT_FALLBACK_MARKDOWN. */
export const EMPTY_ASSISTANT_RESPONSE_MESSAGE =
  "I couldn't generate a response. Please try again or rephrase your question.";

/** QA override for agent loop when entitled plan is unavailable (story 3.13). */
export const CRM_CHAT_FORCE_AGENT_LOOP_KEY = "crm-chat:force-agent-loop";

/** QA override for tool trace panel (story 3.23). */
export const CRM_CHAT_FORCE_TOOL_TRACE_KEY = "crm-chat:force-tool-trace";

/** QA override for intent suggestions panel (story 3.51). */
export const CRM_CHAT_FORCE_INTENT_SUGGESTIONS_KEY = "crm-chat:force-intent-suggestions";

/** Max prior turns sent in ChatTurnRequest.history to avoid oversized payloads. */
export const CHAT_TURN_HISTORY_MAX_MESSAGES = 40;

export const MANAGING_PROMPT_CATEGORIES: ManagingPromptCategory[] = [
  "sales",
  "success",
  "product",
  "marketing",
];

interface PromptManifestOverride {
  recordTypes?: CrmRecordType[];
  requiresRecord?: boolean;
  chatEnabled?: boolean;
}

/** Non-blocking honesty copy for ungated prompts that still over-claim external data (story 3.17 AC3). */
export const PROMPT_CONTEXT_HONESTY_BADGES: Partial<Record<string, string>> = {
  "objection-handling": "CRM only — public web research not connected",
  "onboarding-prep": "Limited to CRM record until email and calendar enrichers ship",
  "qbr-prep": "Limited to CRM record until email and calendar enrichers ship",
};

const TRANSCRIPT_HARD_GATE_KEYS = ["calendar", "meeting", "webResearch", "productUsage"];

/** Transcript is setup in UI (picker), not a hard-disabled gallery gate (story 3.17 UX). */
export function isTranscriptSoftGatedPrompt(
  prompt: Pick<ManagingPromptCatalogEntry, "contextRequirements" | "requiresCallRecording">
): boolean {
  const reqs = prompt.contextRequirements ?? [];
  if (prompt.requiresCallRecording) {
    return true;
  }
  if (!reqs.includes("transcript")) {
    return false;
  }
  return !TRANSCRIPT_HARD_GATE_KEYS.some((k) => reqs.includes(k));
}

export const TRANSCRIPT_SETUP_HINT =
  "Select a call or meeting below (uses transcript or activity description/notes)";

/** True when BFF registry lists `transcript` in contextRequirements (story 3.17 — no duplicate slug list). */
export function promptRequiresCallRecording(
  prompt: Pick<ManagingPromptCatalogEntry, "promptType" | "contextRequirements" | "requiresCallRecording">
): boolean {
  if (prompt.requiresCallRecording === true) {
    return true;
  }
  return prompt.contextRequirements?.includes("transcript") === true;
}

/**
 * Suggested record-type scope per prompt-type (spec §10.2). `requiresRecord`
 * is true when the prompt is meaningless without a CRM record; transcript
 * dependent prompts are intentionally `chatEnabled: false` in v1.
 */
export const PROMPT_MANIFEST_OVERRIDES: Record<string, PromptManifestOverride> = {
  "sales-coach": {
    recordTypes: ["SALE", "COMPANY"],
    requiresRecord: true,
  },
  "run-account-research": { recordTypes: ["COMPANY"], requiresRecord: true },
  "follow-up-email": {
    recordTypes: ["SALE", "COMPANY", "CONTACT"],
    requiresRecord: true,
  },
  "daily-prep": {},
  "meeting-prep": { recordTypes: ["SALE", "COMPANY"], requiresRecord: true },
  "product-engagement": { recordTypes: ["COMPANY"], requiresRecord: true },
  "what-did-that-mean": {},
  "what-did-i-miss": {},
  "qbr-prep": { recordTypes: ["COMPANY"], requiresRecord: true },
  "shipped-feature-outeach": { recordTypes: ["COMPANY"], requiresRecord: false },
  "onboarding-prep": { recordTypes: ["COMPANY"], requiresRecord: true },
  "objection-handling": { recordTypes: ["SALE"], requiresRecord: true },
  "suggest-next-steps": {
    recordTypes: ["SALE"],
    requiresRecord: true,
  },
  "recap-call": {
    recordTypes: ["SALE", "COMPANY"],
    requiresRecord: false,
  },
  "product-feedback": {
    recordTypes: ["SALE", "COMPANY"],
    requiresRecord: false,
  },
  "deal-brief": { recordTypes: ["SALE"], requiresRecord: true },
  "update-deal": {
    recordTypes: ["SALE"],
    requiresRecord: true,
  },
};

/** Empty-composer fallback — top 3 by record type (story 3.51 AC §2.5). */
export const POPULAR_PROMPTS_BY_RECORD_TYPE: Partial<
  Record<CrmRecordType, readonly string[]>
> = {
  SALE: ["deal-brief", "sales-coach", "follow-up-email"],
  COMPANY: ["run-account-research", "onboarding-prep", "qbr-prep"],
  CONTACT: ["follow-up-email", "objection-handling"],
};

/** When no record is selected on Ask page. */
export const POPULAR_PROMPTS_DEFAULT: readonly string[] = [
  "deal-brief",
  "follow-up-email",
  "objection-handling",
];

export const CUSTOM_PROMPT_ENTRY: ManagingPromptCatalogEntry = {
  promptType: CHAT_PROMPT_CUSTOM_TYPE,
  name: "Custom",
  description: "Ask anything — write your own prompt for the current record.",
  categories: [],
  requiresRecord: false,
  chatEnabled: true,
};

/**
 * Merge raw catalog entries with the manifest overrides above and ensure the
 * synthetic `Custom` entry is always available as the last option.
 */
export function enrichPromptCatalog(
  entries: ManagingPromptCatalogEntry[]
): ManagingPromptCatalogEntry[] {
  const enriched = entries.map<ManagingPromptCatalogEntry>((entry) => {
    const overrides = PROMPT_MANIFEST_OVERRIDES[entry.promptType] ?? {};
    return {
      ...entry,
      recordTypes: entry.recordTypes ?? overrides.recordTypes,
      requiresRecord: entry.requiresRecord ?? overrides.requiresRecord ?? false,
      requiresCallRecording:
        entry.requiresCallRecording ??
        (entry.contextRequirements?.includes("transcript") ? true : undefined) ??
        false,
      contextHonestyBadge:
        entry.contextHonestyBadge ?? PROMPT_CONTEXT_HONESTY_BADGES[entry.promptType],
      chatEnabled: entry.chatEnabled ?? overrides.chatEnabled ?? true,
    };
  });

  const hasCustom = enriched.some((e) => e.promptType === CHAT_PROMPT_CUSTOM_TYPE);
  return hasCustom ? enriched : [...enriched, CUSTOM_PROMPT_ENTRY];
}

/**
 * Filter rule for the gallery / picker: prompt must be chat-enabled,
 * and if a record is selected its type must be supported (or the prompt
 * is record-agnostic).
 */
export function filterPromptsForContext(
  prompts: ManagingPromptCatalogEntry[],
  opts: { recordType?: CrmRecordType | null; category?: ManagingPromptCategory | "all" }
): ManagingPromptCatalogEntry[] {
  const { recordType, category } = opts;
  return prompts.filter((p) => {
    if (p.chatEnabled === false) return false;
    if (category && category !== "all") {
      if (p.promptType === CHAT_PROMPT_CUSTOM_TYPE) return true;
      if (!p.categories?.includes(category)) return false;
    }
    if (recordType && p.recordTypes?.length) {
      if (!p.recordTypes.includes(recordType)) return false;
    }
    return true;
  });
}

export function isPromptUsable(
  prompt: ManagingPromptCatalogEntry,
  recordType?: CrmRecordType | null,
  opts?: { hasCallRecordingWithTranscript?: boolean }
): { usable: boolean; reason?: string; setupReason?: string } {
  const softTranscriptGate = isTranscriptSoftGatedPrompt(prompt);

  if (prompt.runnable === false && !softTranscriptGate) {
    return {
      usable: false,
      reason: prompt.unavailableReason ?? "Not available yet",
    };
  }
  if (prompt.chatEnabled === false) {
    return { usable: false, reason: "Not available in chat yet" };
  }
  if (prompt.requiresRecord && !recordType) {
    return { usable: false, reason: "Select a CRM record to use this prompt" };
  }
  if (recordType && prompt.recordTypes?.length && !prompt.recordTypes.includes(recordType)) {
    const list = prompt.recordTypes.join(", ");
    return { usable: false, reason: `Works with ${list}` };
  }
  if (softTranscriptGate) {
    const hasTranscript =
      prompt.runnable === true || opts?.hasCallRecordingWithTranscript === true;
    if (!hasTranscript) {
      return { usable: true, setupReason: TRANSCRIPT_SETUP_HINT };
    }
  }
  return { usable: true };
}

/** Build execute `context` for a selected call/meeting activity. */
export function buildCallRecordingExecuteContext(activityId: string): Record<string, unknown> {
  return { activityId };
}
